import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, ButtonGroup } from "react-bootstrap";
import "./VistaPreviaImagenClinica.css";

const COLORES = [
    { id: "rojo", color: "#e03131" },
    { id: "azul", color: "#1971c2" },
    { id: "amarillo", color: "#fcc419" },
    { id: "blanco", color: "#ffffff" },
];

const GROSORES = [2, 5, 10];
const ZOOM_MIN = 0.15;
const ZOOM_MAX = 4;

function VistaPreviaImagenClinica({ show, onHide, imageUrl, nombre }) {
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [rotacion, setRotacion] = useState(0);
    const [modoDibujo, setModoDibujo] = useState(false);
    const [color, setColor] = useState(COLORES[0].color);
    const [grosor, setGrosor] = useState(GROSORES[1]);
    const [imgListo, setImgListo] = useState(false);
    const [arrastrando, setArrastrando] = useState(false);
    const [dibujando, setDibujando] = useState(false);
    const [imgSize, setImgSize] = useState({ w: 0, h: 0, innerW: 0, innerH: 0 });

    const viewportRef = useRef(null);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const panInicio = useRef(null);

    const limpiarCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, []);

    const syncCanvasSize = useCallback(() => {
        const canvas = canvasRef.current;
        const { innerW, innerH } = imgSize;
        if (!canvas || !innerW || !innerH) return;

        canvas.width = innerW;
        canvas.height = innerH;
        canvas.style.width = `${innerW}px`;
        canvas.style.height = `${innerH}px`;
    }, [imgSize]);

    const calcularAjusteVista = useCallback((rot = rotacion) => {
        const viewport = viewportRef.current;
        const img = imgRef.current;
        if (!viewport || !img?.naturalWidth) return null;

        const vw = Math.max(viewport.clientWidth - 4, 80);
        const vh = Math.max(viewport.clientHeight - 4, 80);
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        const rotada = rot % 180 !== 0;
        const effW = rotada ? nh : nw;
        const effH = rotada ? nw : nh;

        const scale = Math.min(vw / effW, vh / effH);
        const innerW = Math.max(1, Math.round(effW * scale));
        const innerH = Math.max(1, Math.round(effH * scale));
        const boundW = rotada ? innerH : innerW;
        const boundH = rotada ? innerW : innerH;

        return { innerW, innerH, boundW, boundH };
    }, [rotacion]);

    const aplicarAjusteVista = useCallback((rot = rotacion) => {
        const ajuste = calcularAjusteVista(rot);
        if (!ajuste) return;

        setImgSize({
            w: ajuste.boundW,
            h: ajuste.boundH,
            innerW: ajuste.innerW,
            innerH: ajuste.innerH,
        });
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [calcularAjusteVista, rotacion]);

    useEffect(() => {
        if (show) {
            setZoom(1);
            setPan({ x: 0, y: 0 });
            setRotacion(0);
            setModoDibujo(false);
            setImgListo(false);
            setImgSize({ w: 0, h: 0, innerW: 0, innerH: 0 });
        }
    }, [show, imageUrl]);

    useEffect(() => {
        if (!show || !imgListo) return undefined;
        syncCanvasSize();
        const onResize = () => aplicarAjusteVista();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [show, imgListo, syncCanvasSize, aplicarAjusteVista]);

    useEffect(() => {
        if (!show || !imgListo) return;
        syncCanvasSize();
    }, [show, imgListo, imgSize, syncCanvasSize]);

    useEffect(() => {
        if (!show || !imgListo) return;
        aplicarAjusteVista(rotacion);
        limpiarCanvas();
    }, [rotacion, show, imgListo, aplicarAjusteVista, limpiarCanvas]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport || !show) return undefined;

        const onWheel = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.12 : 0.12;
            setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, +(z + delta).toFixed(2))));
        };

        viewport.addEventListener("wheel", onWheel, { passive: false });
        return () => viewport.removeEventListener("wheel", onWheel);
    }, [show]);

    const puntoEnCanvas = (clientX, clientY) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (clientX - rect.left) * (canvas.width / rect.width),
            y: (clientY - rect.top) * (canvas.height / rect.height),
        };
    };

    const iniciarInteraccion = (clientX, clientY) => {
        if (modoDibujo) {
            setDibujando(true);
            const p = puntoEnCanvas(clientX, clientY);
            const ctx = canvasRef.current.getContext("2d");
            ctx.strokeStyle = color;
            ctx.lineWidth = grosor;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            return;
        }
        setArrastrando(true);
        panInicio.current = { x: clientX - pan.x, y: clientY - pan.y };
    };

    const moverInteraccion = (clientX, clientY) => {
        if (modoDibujo && dibujando) {
            const p = puntoEnCanvas(clientX, clientY);
            const ctx = canvasRef.current.getContext("2d");
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
            return;
        }
        if (arrastrando && panInicio.current) {
            setPan({
                x: clientX - panInicio.current.x,
                y: clientY - panInicio.current.y,
            });
        }
    };

    const finalizarInteraccion = () => {
        setDibujando(false);
        setArrastrando(false);
        panInicio.current = null;
    };

    const onImageLoad = () => {
        setImgListo(true);
        aplicarAjusteVista(0);
        limpiarCanvas();
    };

    const girarIzquierda = () => setRotacion((r) => (r + 270) % 360);
    const girarDerecha = () => setRotacion((r) => (r + 90) % 360);

    const cursorVista = modoDibujo ? "crosshair" : (arrastrando ? "grabbing" : "grab");

    const abrirEnPestanaCompleta = () => {
        if (!imageUrl) return;
        window.open(imageUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            fullscreen="md-down"
            scrollable={false}
            enforceFocus={false}
            restoreFocus={false}
            className="modal-preview-clinica-root"
            dialogClassName="modal-preview-clinica"
            contentClassName="modal-preview-clinica-content"
        >
            <Modal.Header className="preview-modal-header py-2">
                <div className="d-flex align-items-start justify-content-between w-100">
                    <Modal.Title className="mb-0 text-truncate preview-modal-title pe-3">
                        {nombre}
                    </Modal.Title>
                    <div className="preview-header-actions d-flex align-items-center flex-shrink-0">
                        <button
                            type="button"
                            className="preview-btn-header-icon mr-1"
                            title="Abrir en pestaña completa"
                            onClick={abrirEnPestanaCompleta}
                        >
                            <i className="fas fa-external-link-alt" />
                        </button>
                        <button
                            type="button"
                            className="close preview-modal-close"
                            aria-label="Cerrar"
                            onClick={onHide}
                        >
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="p-2">
                <div className="preview-toolbar d-flex flex-wrap align-items-center justify-content-between mb-2 px-1 gap-2">
                    <ButtonGroup size="sm">
                        <Button
                            variant="outline-secondary"
                            title="Alejar"
                            onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - 0.25).toFixed(2)))}
                        >
                            <i className="fas fa-search-minus" />
                        </Button>
                        <Button variant="outline-secondary" disabled style={{ minWidth: 58 }}>
                            {Math.round(zoom * 100)}%
                        </Button>
                        <Button
                            variant="outline-secondary"
                            title="Acercar"
                            onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + 0.25).toFixed(2)))}
                        >
                            <i className="fas fa-search-plus" />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            title="Ajustar a pantalla"
                            onClick={() => aplicarAjusteVista()}
                        >
                            <i className="fas fa-compress" />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            title="Girar a la izquierda"
                            onClick={girarIzquierda}
                        >
                            <i className="fas fa-undo" />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            title="Girar a la derecha"
                            onClick={girarDerecha}
                        >
                            <i className="fas fa-redo" />
                        </Button>
                    </ButtonGroup>

                    <div className="d-flex gap-2 preview-acciones-dibujo">
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            className={`preview-btn-dibujar${modoDibujo ? " active" : ""}`}
                            title="Dibujar sobre la imagen"
                            onClick={() => setModoDibujo((v) => !v)}
                        >
                            <i className="fas fa-pen mr-1" />
                            Dibujar
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            className="preview-btn-borrar"
                            title="Borrar marcas"
                            onClick={limpiarCanvas}
                            disabled={!imgListo}
                        >
                            <i className="fas fa-eraser mr-1" />
                            Borrar
                        </Button>
                    </div>
                </div>

                {modoDibujo && (
                    <div className="d-flex flex-wrap align-items-center mb-2 px-1 gap-2">
                        <span className="small text-muted mr-1">Color:</span>
                        {COLORES.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                title={c.id}
                                onClick={() => setColor(c.color)}
                                style={{
                                    width: 26,
                                    height: 26,
                                    borderRadius: "50%",
                                    border: color === c.color ? "2px solid #228be6" : "2px solid #adb5bd",
                                    background: c.color,
                                    padding: 0,
                                    cursor: "pointer",
                                }}
                            />
                        ))}
                        <span className="small text-muted ml-2 mr-1">Grosor:</span>
                        <ButtonGroup size="sm">
                            {GROSORES.map((g) => (
                                <Button
                                    key={g}
                                    variant={grosor === g ? "secondary" : "outline-secondary"}
                                    onClick={() => setGrosor(g)}
                                >
                                    {g}px
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                )}

                <p className="preview-hint small text-muted mb-2 px-1">
                    <i className="fas fa-info-circle mr-1" />
                    Las marcas y la rotación son temporales y no se guardan. Use la rueda del mouse o los botones para zoom.
                    {!modoDibujo && " Arrastre la imagen para moverla."}
                </p>

                <div
                    ref={viewportRef}
                    className="preview-viewport rounded"
                    style={{
                        overflow: "hidden",
                        background: "#212529",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: cursorVista,
                        touchAction: "none",
                        userSelect: "none",
                    }}
                    onMouseDown={(e) => {
                        if (e.button !== 0) return;
                        e.preventDefault();
                        iniciarInteraccion(e.clientX, e.clientY);
                    }}
                    onMouseMove={(e) => moverInteraccion(e.clientX, e.clientY)}
                    onMouseUp={finalizarInteraccion}
                    onMouseLeave={finalizarInteraccion}
                    onTouchStart={(e) => {
                        if (e.touches.length !== 1) return;
                        iniciarInteraccion(e.touches[0].clientX, e.touches[0].clientY);
                    }}
                    onTouchMove={(e) => {
                        if (e.touches.length !== 1) return;
                        if (modoDibujo || arrastrando) e.preventDefault();
                        moverInteraccion(e.touches[0].clientX, e.touches[0].clientY);
                    }}
                    onTouchEnd={finalizarInteraccion}
                >
                    <div
                        style={{
                            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                            transformOrigin: "center center",
                            position: "relative",
                            lineHeight: 0,
                            width: imgSize.w ? `${imgSize.w}px` : "auto",
                            height: imgSize.h ? `${imgSize.h}px` : "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                width: imgSize.innerW ? `${imgSize.innerW}px` : "auto",
                                height: imgSize.innerH ? `${imgSize.innerH}px` : "auto",
                                transform: `rotate(${rotacion}deg)`,
                                transformOrigin: "center center",
                                position: "relative",
                            }}
                        >
                            {imageUrl && (
                                <img
                                    ref={imgRef}
                                    src={imageUrl}
                                    alt={nombre}
                                    draggable={false}
                                    onLoad={onImageLoad}
                                    style={{
                                        width: imgSize.innerW ? `${imgSize.innerW}px` : "auto",
                                        height: imgSize.innerH ? `${imgSize.innerH}px` : "auto",
                                        display: "block",
                                        pointerEvents: "none",
                                        objectFit: "contain",
                                    }}
                                />
                            )}
                            <canvas
                                ref={canvasRef}
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    pointerEvents: "none",
                                }}
                            />
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default VistaPreviaImagenClinica;
